import React, { useMemo, useRef, useState } from 'react';

import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import PropTypes from 'prop-types';

// Components
import { Mui, PerfectScrollbar } from 'components';
import EmptyStateResults from 'components/common/EmptyState/Results';
import { ProgressWrapper } from 'components/layouts';

// Local components
import ColumnCustomizer from './ColumnCustomizer';
import { resolveListingEmptyIcon } from './emptyState';
import useColumnPreferences from './useColumnPreferences';

const coreRowModel = getCoreRowModel();
const FIT_CONTENT_STYLE = { width: '1%', whiteSpace: 'nowrap' };
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
const DEFAULT_EMPTY_TITLE = 'Nenhum resultado encontrado';
const DEFAULT_EMPTY_DESCRIPTION = 'Tente ajustar a busca ou os filtros da listagem.';
const EMPTY_ROW_SELECTION = {};
const EMPTY_STATE_SHAPE = PropTypes.shape({
  icon: PropTypes.string,
  customIcon: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
  action: PropTypes.shape({
    label: PropTypes.node,
    onClick: PropTypes.func,
  }),
});
const DEFAULT_GET_ROW_ID = (row, index) =>
  String(row.uid || row.id || row.code || row.orderNumber || index);

const resolvePaginationMode = (pagination, query, setQuery) => {
  if (pagination) return pagination;
  return query && setQuery ? 'server' : 'none';
};

const listingMinHeight = (isPending, rowCount) => (isPending && !rowCount ? 220 : undefined);

const resolveVisibleListing = ({ data, loading, tableId, scopeKey, settled, listIdentity }) => {
  const nextIdentity = `${tableId}\0${scopeKey || ''}`;
  const previous = listIdentity === nextIdentity ? settled : null;
  const hasResultList = Array.isArray(data?.results);
  const isPending = Boolean(loading) || (loading == null && data == null);
  const visibleData = hasResultList || !isPending ? data : previous;

  const { results = [], count = 0 } = visibleData || {};

  return {
    results,
    count,
    isPending,
    settled: hasResultList ? data : previous,
    listIdentity: nextIdentity,
  };
};

const getPagedRows = (results, page, pageSize, paginationMode) => {
  if (paginationMode !== 'client') return results;

  const start = (page - 1) * pageSize;
  return results.slice(start, start + pageSize);
};

const useStyles = Mui.makeStyles(theme => ({
  clickableRow: { '&:hover': { cursor: 'pointer' } },
  tableToolbar: {
    alignItems: 'center',
    backgroundColor: theme.palette.grey[300],
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'flex-end',
    minHeight: 48,
    padding: theme.spacing(0, 2),
  },
  emptyCell: { borderBottom: 0, padding: 0 },
}));

const toColumnDef = ({ id, label, cell, pinned, ...meta }) => ({
  id,
  header: label,
  cell,
  enableHiding: !pinned,
  meta,
});

const getCellProps = ({ columnDef }) => ({
  align: columnDef.meta?.align || 'left',
  padding: columnDef.meta?.padding,
  style: columnDef.meta?.fitContent ? FIT_CONTENT_STYLE : undefined,
});

function ListingHead({ table, selectedRows, renderBulkHeader, visibleColumnsCount, cardRef }) {
  if (selectedRows.length > 0 && renderBulkHeader) {
    return (
      <Mui.TableRow>
        <Mui.TableCell padding="checkbox">
          <Mui.Checkbox
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        </Mui.TableCell>
        <Mui.TableCell colSpan={Math.max(visibleColumnsCount - 1, 1)}>
          {renderBulkHeader({
            table,
            selectedRows: selectedRows.map(row => row.original),
            cardWidth: cardRef.current?.clientWidth || 'auto',
          })}
        </Mui.TableCell>
      </Mui.TableRow>
    );
  }

  return table.getHeaderGroups().map(headerGroup => (
    <Mui.TableRow key={headerGroup.id}>
      {headerGroup.headers
        .filter(header => !header.isPlaceholder)
        .map(header => (
          <Mui.TableCell key={header.id} {...getCellProps(header.column)}>
            {flexRender(header.column.columnDef.header, header.getContext())}
          </Mui.TableCell>
        ))}
    </Mui.TableRow>
  ));
}

ListingHead.propTypes = {
  table: PropTypes.object.isRequired,
  selectedRows: PropTypes.array.isRequired,
  renderBulkHeader: PropTypes.func,
  visibleColumnsCount: PropTypes.number.isRequired,
  cardRef: PropTypes.object.isRequired,
};

function EmptyRow({ className, colSpan, emptyState, tableId }) {
  return (
    <Mui.TableRow>
      <Mui.TableCell className={className} colSpan={colSpan}>
        <EmptyStateResults
          embedded
          action={emptyState?.action}
          customIcon={emptyState?.customIcon}
          description={emptyState?.description || DEFAULT_EMPTY_DESCRIPTION}
          icon={emptyState?.icon || resolveListingEmptyIcon(tableId)}
          title={emptyState?.title || DEFAULT_EMPTY_TITLE}
        />
      </Mui.TableCell>
    </Mui.TableRow>
  );
}

EmptyRow.propTypes = {
  className: PropTypes.string,
  colSpan: PropTypes.number,
  emptyState: EMPTY_STATE_SHAPE,
  tableId: PropTypes.string,
};

const SELECT_COLUMN = {
  id: 'select',
  header: ({ table }) => (
    <Mui.Checkbox
      checked={table.getIsAllPageRowsSelected()}
      indeterminate={table.getIsSomePageRowsSelected()}
      onChange={table.getToggleAllPageRowsSelectedHandler()}
    />
  ),
  cell: ({ row }) => (
    <Mui.Checkbox
      checked={row.getIsSelected()}
      disabled={!row.getCanSelect()}
      onClick={event => event.stopPropagation()}
      onChange={row.getToggleSelectedHandler()}
    />
  ),
  enableHiding: false,
  meta: { padding: 'checkbox', fitContent: true },
};

function DataListing({
  tableId,
  scopeKey,
  columns,
  data,
  loading,
  meta,
  query,
  setQuery,
  pagination,
  getRowId,
  onRowClick,
  getRowClassName,
  tabs,
  beforeTable,
  enableRowSelection,
  rowSelection,
  onRowSelectionChange,
  renderBulkHeader,
  emptyState,
}) {
  // Hooks
  const classes = useStyles();
  const cardRef = useRef(null);
  const settledRef = useRef(null);
  const listIdentityRef = useRef('');
  const { columnVisibility, columnOrder, savePreferences } = useColumnPreferences({
    tableId,
    scopeKey,
    columns,
  });

  // States
  const [customizerOpen, setCustomizerOpen] = useState(false);

  // Constants
  const paginationMode = resolvePaginationMode(pagination, query, setQuery);
  const page = Number(query?.page) || 1;
  const pageSize = Number(query?.page_size) || 25;
  const visibleListing = resolveVisibleListing({
    data,
    loading,
    tableId,
    scopeKey,
    settled: settledRef.current,
    listIdentity: listIdentityRef.current,
  });
  const { results, count: dataCount, isPending } = visibleListing;

  settledRef.current = visibleListing.settled;
  listIdentityRef.current = visibleListing.listIdentity;

  const tableData = useMemo(
    () => getPagedRows(results, page, pageSize, paginationMode),
    [results, page, pageSize, paginationMode],
  );
  const count = paginationMode === 'client' ? results.length : dataCount;
  const showPagination = paginationMode !== 'none' && query && setQuery;
  const resolveRowId = getRowId || DEFAULT_GET_ROW_ID;

  const tableColumns = useMemo(() => {
    const columnDefs = columns.map(toColumnDef);

    return enableRowSelection ? [SELECT_COLUMN, ...columnDefs] : columnDefs;
  }, [columns, enableRowSelection]);

  const columnPinning = useMemo(() => {
    const left = [
      ...(enableRowSelection ? ['select'] : []),
      ...columns.filter(column => column.pinned).map(column => column.id),
    ];

    return left.length ? { left, right: [] } : undefined;
  }, [columns, enableRowSelection]);

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    meta,
    getRowId: resolveRowId,
    state: {
      columnVisibility,
      columnOrder,
      ...(columnPinning ? { columnPinning } : {}),
      rowSelection: rowSelection || EMPTY_ROW_SELECTION,
    },
    onRowSelectionChange,
    enableRowSelection,
    manualPagination: true,
    autoResetPageIndex: false,
    getCoreRowModel: coreRowModel,
  });

  const { rows } = table.getRowModel();
  const selectedRows = table.getSelectedRowModel().rows;
  const visibleColumnsCount = table.getVisibleLeafColumns().length;

  // Actions
  const handleChangePage = (event, newPage) => {
    if (selectedRows.length) table.resetRowSelection();
    setQuery(prevQuery => ({ ...prevQuery, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = event => {
    if (selectedRows.length) table.resetRowSelection();
    setQuery(prevQuery => ({ ...prevQuery, page: 1, page_size: Number(event.target.value) }));
  };

  return (
    <Mui.Card ref={cardRef}>
      {!!tabs && (
        <>
          <Mui.Tabs
            onChange={tabs.onChange}
            scrollButtons="auto"
            textColor="secondary"
            value={tabs.value}
            variant="scrollable">
            {tabs.items.map(tab => (
              <Mui.Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Mui.Tabs>
          <Mui.Divider />
        </>
      )}
      {!!beforeTable && <Mui.Box p={2}>{beforeTable}</Mui.Box>}
      <Mui.Box className={classes.tableToolbar}>
        <Mui.Button
          color="secondary"
          disableElevation
          onClick={() => setCustomizerOpen(true)}
          startIcon={<Mui.Icon fontSize="small">view_column</Mui.Icon>}
          variant="outlined">
          Colunas
        </Mui.Button>
      </Mui.Box>
      <Mui.Divider />
      <PerfectScrollbar>
        <ProgressWrapper
          loading={isPending}
          minHeight={listingMinHeight(isPending, results.length)}>
          <Mui.Box minWidth={700}>
            <Mui.Table>
              <Mui.TableHead>
                <ListingHead
                  table={table}
                  selectedRows={selectedRows}
                  renderBulkHeader={renderBulkHeader}
                  visibleColumnsCount={visibleColumnsCount}
                  cardRef={cardRef}
                />
              </Mui.TableHead>
              <Mui.TableBody>
                {!isPending && rows.length === 0 && (
                  <EmptyRow
                    className={classes.emptyCell}
                    colSpan={visibleColumnsCount}
                    emptyState={emptyState}
                    tableId={tableId}
                  />
                )}
                {rows.map(row => (
                  <Mui.TableRow
                    key={row.id}
                    hover
                    className={clsx(
                      onRowClick && classes.clickableRow,
                      getRowClassName?.(row.original),
                    )}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}>
                    {row.getVisibleCells().map(cell => (
                      <Mui.TableCell key={cell.id} {...getCellProps(cell.column)}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                ))}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.Box>
        </ProgressWrapper>
      </PerfectScrollbar>
      {showPagination && (
        <Mui.TablePagination
          component="div"
          count={count}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={Math.max(page - 1, 0)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      )}
      <ColumnCustomizer
        open={customizerOpen}
        onClose={() => setCustomizerOpen(false)}
        columns={columns}
        visibility={columnVisibility}
        order={columnOrder}
        onSave={savePreferences}
      />
    </Mui.Card>
  );
}

DataListing.propTypes = {
  tableId: PropTypes.string.isRequired,
  scopeKey: PropTypes.string,
  columns: PropTypes.array.isRequired,
  data: PropTypes.object,
  loading: PropTypes.bool,
  meta: PropTypes.object,
  query: PropTypes.object,
  setQuery: PropTypes.func,
  pagination: PropTypes.oneOf(['server', 'client', 'none']),
  getRowId: PropTypes.func,
  onRowClick: PropTypes.func,
  getRowClassName: PropTypes.func,
  tabs: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    onChange: PropTypes.func,
    items: PropTypes.array,
  }),
  beforeTable: PropTypes.node,
  enableRowSelection: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  rowSelection: PropTypes.object,
  onRowSelectionChange: PropTypes.func,
  renderBulkHeader: PropTypes.func,
  emptyState: EMPTY_STATE_SHAPE,
};

DataListing.defaultProps = {
  enableRowSelection: false,
  emptyState: null,
};

export default DataListing;
